import { useInventoryPracticeDataset, INVENTORY_PRACTICE_PREFIX } from '../../onboarding/practice/inventoryPractice';
import { usePracticeDataset } from '../../onboarding/practice/practiceDataset';
import { PracticeScopeProvider } from '../../onboarding/practice/practiceScope';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  tertiaryText,
  semiBoldText,
  smallTextSize,
  baseTextSize,
  xxLargeTextSize,
  borderPrimary,
  greenTextColor,
  lightGreenBG,
  iconBgGreen,
  iconBgPurple,
  iconBgAmber,
  textBlue,
  quaternaryText,
  regularText,
} from '../../../styling/stylingHelper';
import { StatCard } from '../../ui/StatCard';
import { CardWithHeader } from '../../ui/CardWithHeader';
import { TableComponent } from '../../ui/TableComponent';
import { TabbedSections } from '../../ui/TabbedSections';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useInventoryViewStore as useInventoryStore } from '../../onboarding/practice/inventoryPractice';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useLoadCostPrice, useLoadSubrecipes } from '../../../utils/dataLoadingUtils';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessInventoryPage } from '../../../services/accessManagement/accessManagement';
import { CountReviewProduct } from '../../../services/inventory/inventoryService';
import { CustomIngredient } from '../../../services/customIngredients/customIngredientsService';
import { ValueByCategoryCard, CategoryMode, CategorySelection, buildCategoryBreakdown } from './ValueByCategoryCard';
import { InventoryAttentionCard } from './InventoryAttentionCard';
import ReLinkInventoryProductModal, { relinkTargetFromReviewRow } from './ReLinkInventoryProductModal';
import SetInventoryPriceModal, { setPriceTargetFromReviewRow } from './SetInventoryPriceModal';
import ProductSetupModal from '../costPrice/new/CostPrice/ProductSetupModal';
import CustomIngredientDetailModal from '../costPrice/new/CostPrice/CustomIngredientDetailModal';
import {
  buildBaseProductColumns,
  buildExpandedProductColumns,
  buildSupplierFilterOptions,
  buildInvCategoryFilterOptions,
  applyProductColumnFilters,
  sortProducts,
  actionColumn,
  renderProductActions,
  buildCostPriceInventoryKeys,
  loadCustomIngredientForRow,
  productCategoryLabel,
  setupMatchForReviewRow,
  ProductCountMode,
} from './inventoryReviewShared';
import { InventoryProductMobileCard, InventoryProductSearchInput } from './inventoryProductMobileCard';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import AddCountProductModal from './AddCountProductModal';
import { walkthroughAddedProductKey, walkthroughCountProduct } from '../../onboarding/inventoryValues';

const MOBILE_PRODUCTS_PREVIEW = 3;
const MOBILE_PRODUCTS_BATCH = 20;

function shortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Section detail — and, with `allProducts` (route `/inventory/all-products`), the exact same
 * page scoped to EVERY counted product of the current selection instead of one section's.
 * The section-only pieces fall away in that mode because there is no single section behind
 * them: the share-of-total KPI, the add-product action (it writes into one saved count) and
 * the section-named attention title.
 */
const InventorySectionDetailPage = ({ allProducts = false }: { allProducts?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sectionId } = useParams<{ sectionId: string }>();
  const [searchParams] = useSearchParams();
  const queryGroupId = searchParams.get('groupId');
  const querySessionId = searchParams.get('sessionId');
  const queryBaselineSessionId = searchParams.get('baselineSessionId');

  const { backendUser, accountSettings } = useAuth();
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  const hasAccess = canAccessInventoryPage(backendUser, accountSettings);

  const { countReview, loadCountReview, removeCountProduct, invalidateCountReview } = useInventoryStore();
  const data = countReview.data;

  // Cost prices + subrecipes feed the "Not setup" attention tab (linked products
  // that no cost price analysis references → leakage would always expect 0).
  useLoadCostPrice();
  useLoadSubrecipes();
  const practice = useInventoryPracticeDataset();
  const practiceActive = !!practice;
  const practiceCost = usePracticeDataset(!!practice);
  const realCostPriceList = useCostPriceStore((s) => s.costPriceList);
  const costPriceList = practiceCost?.costPriceList ?? realCostPriceList;
  const costPriceLoadedFor = useCostPriceStore((s) => s.currentRestaurantId);
  const reloadCustomIngredients = useCostPriceStore((s) => s.reloadCustomIngredients);
  const realSubrecipesById = useSubrecipesStore((s) => s.subrecipesById);
  const subrecipesById = practiceCost?.subrecipesById ?? realSubrecipesById;
  const subrecipesLoadedFor = useSubrecipesStore((s) => s.currentRestaurantId);

  // Undefined until BOTH stores hold this restaurant's data — hides the tab
  // instead of flashing every linked product as "not setup" while loading.
  const costPriceKeys = useMemo(() => {
    if (!practice && (!restaurantId || costPriceLoadedFor !== restaurantId || subrecipesLoadedFor !== restaurantId)) return undefined;
    return buildCostPriceInventoryKeys(costPriceList, subrecipesById);
  }, [restaurantId, costPriceLoadedFor, subrecipesLoadedFor, costPriceList, subrecipesById, practice]);

  const [categoryMode, setCategoryMode] = useState<CategoryMode>('inventory');
  const [activeCategory, setActiveCategory] = useState<CategorySelection | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Default: highest value on top (most relevant products first).
  const [sortColumn, setSortColumn] = useState<string | null>('value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>('desc');
  const [supplierFilters, setSupplierFilters] = useState<string[]>([]);
  const [invCategoryFilters, setInvCategoryFilters] = useState<string[]>([]);
  const [isFullPageView, setIsFullPageView] = useState(false);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_PRODUCTS_PREVIEW);
  const [relinkProduct, setRelinkProduct] = useState<CountReviewProduct | null>(null);
  const [setPriceProduct, setSetPriceProduct] = useState<CountReviewProduct | null>(null);
  const [setupProduct, setSetupProduct] = useState<CountReviewProduct | null>(null);
  // The custom ingredient behind an "Edit" click — resolved from the row before the modal
  // opens, so it mounts in edit mode with the real ingredient.
  const [editingCustomIngredient, setEditingCustomIngredient] = useState<CustomIngredient | null>(null);
  // Counts (raw counted number + € per counted unit) ⇄ Quantity (standardised kg/L/pcs +
  // € per std unit). Counts is the default: it is what staff actually typed.
  const [countMode, setCountMode] = useState<ProductCountMode>('counts');
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load the count-review view for the deep-linked selection. When navigating from the
  // main page the store already holds this exact selection → cache hit, no refetch.
  useEffect(() => {
    if (!restaurantId || isAllRestaurants || (!practice && sectionId?.startsWith(INVENTORY_PRACTICE_PREFIX))) return;
    loadCountReview(restaurantId, { groupId: queryGroupId, sessionId: querySessionId, baselineSessionId: queryBaselineSessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants, queryGroupId, querySessionId, queryBaselineSessionId]);

  const sectionMeta = useMemo(
    () => data?.sections.find((s) => s.sectionId === sectionId) ?? null,
    [data, sectionId]
  );

  const sectionProducts = useMemo(
    () => (allProducts ? (data?.products ?? []) : (data?.products ?? []).filter((p) => p.sectionId === sectionId)),
    [data, sectionId, allProducts]
  );

  const breakdown = useMemo(() => buildCategoryBreakdown(sectionProducts), [sectionProducts]);

  // Products already in this section's saved count — the add modal offers the rest.
  const countedKeys = useMemo(() => new Set(sectionProducts.map((p) => p.productKey)), [sectionProducts]);

  // Products table: category filter (click-to-filter) → column filters → search → sort.
  // The category filter only affects the products table; the attention tabs stay
  // section-scoped (per spec).
  const categoryFilteredProducts = useMemo(() => {
    if (!activeCategory) return sectionProducts;
    return sectionProducts.filter((p) =>
      activeCategory.mode === 'inventory'
        ? (p.inventoryCategoryId ?? null) === (activeCategory.categoryId ?? null)
        : (p.productCategory ?? 'uncategorized') === activeCategory.productKey
    );
  }, [sectionProducts, activeCategory]);

  const columnFilteredProducts = useMemo(
    () =>
      applyProductColumnFilters(t, categoryFilteredProducts, {
        suppliers: supplierFilters,
        invCategories: invCategoryFilters,
      }),
    [t, categoryFilteredProducts, supplierFilters, invCategoryFilters]
  );

  const searchedProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return columnFilteredProducts;
    return columnFilteredProducts.filter(
      (p) =>
        p.productName.toLowerCase().includes(q) ||
        (p.supplier.name ?? '').toLowerCase().includes(q) ||
        (p.supplier.platform ?? '').toLowerCase().includes(q)
    );
  }, [columnFilteredProducts, searchQuery]);

  const sortedProducts = useMemo(
    () => sortProducts(searchedProducts, sortColumn, sortDirection),
    [searchedProducts, sortColumn, sortDirection]
  );

  const mobileDisplayProducts = useMemo(
    () => sortedProducts.slice(0, mobileVisibleCount),
    [sortedProducts, mobileVisibleCount]
  );

  useEffect(() => {
    setMobileVisibleCount(MOBILE_PRODUCTS_PREVIEW);
  }, [searchQuery, activeCategory, supplierFilters, invCategoryFilters]);

  useEffect(() => {
    if (!practiceActive) return;
    const onOpenSection = (e: Event) => {
      const detail = (e as CustomEvent<{ sectionId?: string; groupId?: string }>).detail;
      if (!detail?.sectionId || allProducts) return;
      if (detail.sectionId === sectionId) return;
      const params = new URLSearchParams();
      if (detail.groupId) params.set('groupId', detail.groupId);
      const qs = params.toString();
      navigate(`/inventory/section/${detail.sectionId}${qs ? `?${qs}` : ''}`);
    };
    const onViewAll = (e: Event) => {
      const full = (e as CustomEvent<{ full?: string }>).detail?.full;
      setIsFullPageView(full !== 'false');
    };
    const onCountMode = (e: Event) => {
      const mode = (e as CustomEvent<{ mode?: ProductCountMode }>).detail?.mode;
      if (mode === 'counts' || mode === 'quantity') setCountMode(mode);
    };
    const onAddProduct = () => setIsAddProductOpen(true);
    const onReset = () => { setIsAddProductOpen(false); setRelinkProduct(null); setSetupProduct(null); setSetPriceProduct(null); setSearchQuery(''); setSupplierFilters([]); setInvCategoryFilters([]); setSortColumn('value'); setSortDirection('desc'); };
    window.addEventListener('onboarding:inventory-reset-view', onReset);
    window.addEventListener('onboarding:inventory-close-all', onReset);
    window.addEventListener('onboarding:inventory-open-section', onOpenSection);
    window.addEventListener('onboarding:inventory-section-view-all', onViewAll);
    window.addEventListener('onboarding:inventory-section-mode', onCountMode);
    window.addEventListener('onboarding:inventory-section-add-product', onAddProduct);
    return () => {
      window.removeEventListener('onboarding:inventory-reset-view', onReset);
      window.removeEventListener('onboarding:inventory-close-all', onReset);
      window.removeEventListener('onboarding:inventory-open-section', onOpenSection);
      window.removeEventListener('onboarding:inventory-section-view-all', onViewAll);
      window.removeEventListener('onboarding:inventory-section-mode', onCountMode);
      window.removeEventListener('onboarding:inventory-section-add-product', onAddProduct);
    };
  }, [allProducts, navigate, sectionId, practiceActive]);

  // Filter dropdown options span the whole section (stable while filters are applied).
  const supplierOptions = useMemo(() => buildSupplierFilterOptions(sectionProducts), [sectionProducts]);
  const invCategoryOptions = useMemo(() => buildInvCategoryFilterOptions(t, sectionProducts), [t, sectionProducts]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      // First click sorts highest-first; the name column starts A→Z.
      setSortDirection(column === 'productName' ? 'asc' : 'desc');
    }
  };

  // Remove a product counted by mistake from the saved count. Confirmed first: it rewrites
  // history (the count total, consumption and previous-session comparisons all move).
  const handleDeleteRow = async (row: CountReviewProduct) => {
    if (!restaurantId || !row.countId) return;
    if (!window.confirm(t('inventory.deleteCountItemConfirm', { product: row.productName }))) return;
    setDeleteError(null);
    try {
      await removeCountProduct(row.countId, restaurantId, row.productKey);
    } catch (err) {
      console.error('Failed to remove product from inventory count', err);
      setDeleteError(t('inventory.deleteCountItemFailed'));
    }
  };

  // "Edit" on a custom-ingredient row: resolve the ingredient behind the row, then open the
  // same side modal the Manage ingredients page uses. Nothing opens when it can't be loaded.
  const openCustomIngredientEditor = async (row: CountReviewProduct) => {
    if (!restaurantId) return;
    const ingredient = await loadCustomIngredientForRow(row, restaurantId);
    if (ingredient) setEditingCustomIngredient(ingredient);
  };

  // Saved: the row's name / supplier / price come from the review payload, so it has to be
  // refetched, and the cost-price store's copy of the ingredient is now stale.
  const handleCustomIngredientSaved = async () => {
    if (!restaurantId) return;
    await invalidateCountReview(restaurantId);
    await reloadCustomIngredients();
  };

  // Products-table actions: "Link" on any not-linked row (order rows with no live order
  // match AND custom-linked rows) + "Edit" on custom-ingredient rows + "Set price" on
  // missing-price rows + "Setup" on not-setup rows (linked but referenced by no cost price
  // analysis) + "Delete" to take a wrongly counted product out of the count.
  const relinkAction = (row: CountReviewProduct) =>
    renderProductActions(t, row, {
      onRelink: setRelinkProduct,
      onEditCustom: openCustomIngredientEditor,
      onSetPrice: setSetPriceProduct,
      onSetup:
        costPriceKeys && row.linked && !costPriceKeys.has(row.productKey) ? setSetupProduct : undefined,
      onDelete: handleDeleteRow,
    });
  const getMobileSetupHandler = (row: CountReviewProduct) =>
    costPriceKeys && row.linked && !costPriceKeys.has(row.productKey) ? setSetupProduct : undefined;
  const columnOpts = useMemo(
    () => ({
      sortable: true,
      supplierFilter: {
        selectedValues: supplierFilters,
        onValuesChange: setSupplierFilters,
        availableOptions: supplierOptions,
        valueType: 'default' as const,
        filterLabel: t('inventory.colSupplier'),
      },
      invCategoryFilter: {
        selectedValues: invCategoryFilters,
        onValuesChange: setInvCategoryFilters,
        availableOptions: invCategoryOptions,
        valueType: 'default' as const,
        filterLabel: t('inventory.colInvCategory'),
      },
      productNameSort: {
        active: sortColumn === 'productName' ? sortDirection : null,
        onToggle: () => handleSort('productName'),
      },
      // Inline-editable raw count (staff correct miscounts straight from this table).
      countEdit: restaurantId ? { restaurantId } : undefined,
      countOnboardingId: (row: CountReviewProduct) =>
        walkthroughCountProduct()?.productKey === row.productKey ? 'inventory-section-count' : undefined,
      // Inline-editable product name — renames the line in THIS saved count only (the
      // /inventory/edit rename covers future counts).
      nameEdit: restaurantId ? { restaurantId } : undefined,
      countMode,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, supplierFilters, invCategoryFilters, supplierOptions, invCategoryOptions, sortColumn, sortDirection, restaurantId, countMode]
  );
  const baseColumns = useMemo(
    () => [...buildBaseProductColumns(t, columnOpts), actionColumn(t, relinkAction)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, columnOpts, costPriceKeys]
  );
  const expandedColumns = useMemo(
    () => [...buildExpandedProductColumns(t, columnOpts), actionColumn(t, relinkAction)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, columnOpts, costPriceKeys]
  );

  // Switching the category-mode toggle drops a stale filter from the other mode.
  const handleModeChange = (mode: CategoryMode) => {
    setCategoryMode(mode);
    setActiveCategory(null);
  };

  const handleCategorySelect = (selection: CategorySelection) => {
    setActiveCategory((prev) => (prev?.rowId === selection.rowId ? null : selection));
  };

  if (!practice && sectionId?.startsWith(INVENTORY_PRACTICE_PREFIX)) return <Navigate to="/inventory?tab=review" replace />;

  if (!hasAccess) return <Navigate to="/" />;
  if (isAllRestaurants) return <Navigate to="/inventory" replace />;
  if (!restaurantId) return null;

  const isLoadingReview = countReview.isLoading && !data;

  // Header context line: "{group} · {session date | Latest}". All-products mode has no section
  // to read the group off, so it comes from the selection (any section of that group carries
  // its name); no group selected = the whole restaurant.
  const groupName = allProducts
    ? data?.selection.groupId
      ? data.sections.find((s) => s.groupId === data.selection.groupId)?.groupName ?? t('inventory.ungrouped')
      : t('inventory.allInventories')
    : sectionMeta?.groupName ?? t('inventory.ungrouped');
  const sessionLabel =
    data?.selection.sessionId && data.selection.completedAt
      ? shortDate(new Date(data.selection.completedAt))
      : t('inventory.selectorLatest');

  // KPIs
  const productsCounted = sectionProducts.length;
  const productsNotLinked = sectionProducts.filter((p) => !p.linked).length;
  // Below-minimum is deliberately NOT surfaced per-section (the minimum is a
  // cross-section total — see M63 data rule); the 4th KPI shows missing price instead.
  const missingPriceCount = sectionProducts.filter((p) => p.missingPrice).length;
  const sharePct = sectionMeta ? (sectionMeta.share * 100).toFixed(1) : '0.0';
  // All products IS the selection's total — summed the same way the server builds the KPI,
  // so it matches the total on the inventory page exactly.
  const totalValue = allProducts
    ? sectionProducts.reduce((sum, p) => sum + p.value, 0)
    : sectionMeta?.totalValue ?? 0;
  const productsTitle = allProducts ? t('inventory.allProductsTitle') : t('inventory.productsInSection');

  // Back arrow inline with the title — same pattern as TopSection's backButton
  // (OrderHistoryProductsPage and other detail pages). The "{group} · {session}"
  // context sits top-right so the KPI row is the first thing under the title.
  const pageHeader = (title: string, context?: string) => (
    <>
    <MobilePageHeader title={title} />
    <div className="mb-6">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex">
          <ArrowLeft className="w-6 h-6 mt-1 mr-2 cursor-pointer" onClick={() => navigate('/inventory')} />
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>{title}</h1>
        </div>
        {context && <p className={cn(smallTextSize, secondaryText, 'mt-1.5 text-right flex-none')}>{context}</p>}
      </div>
      <p className={cn(baseTextSize, secondaryText, 'text-[#667085] dark:text-textTertiary-dark')}>{t(allProducts ? 'inventory.allProductsSubtitle' : 'inventory.sectionPageSubtitle')}</p>
    </div>
    </>
  );

  if (!allProducts && !isLoadingReview && !sectionMeta) {
    return (
      <div>
        {pageHeader(t('inventory.sectionNotFound'))}
        <CardWithHeader title={t('inventory.sectionNotFound')}>
          <p className={cn(baseTextSize, tertiaryText, 'py-8 text-center')}>{t('inventory.sectionNotFoundDesc')}</p>
        </CardWithHeader>
      </div>
    );
  }

  return (
    <PracticeScopeProvider value={!!practice}>
    <div>
      {practice && <p role="status" className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">{t('onboarding.practiceNotice')}</p>}
      {/* Header */}
      {pageHeader(allProducts ? t('inventory.allProductsTitle') : sectionMeta?.sectionName ?? '', `${groupName} · ${sessionLabel}`)}

      {/* KPI row */}
      <div className="mb-5 sm:mb-6 grid gap-2.5 md:gap-6 grid-cols-2 xl:grid-cols-4">
        <div className="h-full min-w-0" data-onboarding="inventory-section-kpis inventory-section-kpi-value">
        <StatCard
          icon={
            <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]' viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.6673 17.3359C17.5562 17.3359 16.6118 16.947 15.834 16.1693C15.0562 15.3915 14.6673 14.447 14.6673 13.3359C14.6673 12.2248 15.0562 11.2804 15.834 10.5026C16.6118 9.72483 17.5562 9.33594 18.6673 9.33594C19.7784 9.33594 20.7229 9.72483 21.5007 10.5026C22.2784 11.2804 22.6673 12.2248 22.6673 13.3359C22.6673 14.447 22.2784 15.3915 21.5007 16.1693C20.7229 16.947 19.7784 17.3359 18.6673 17.3359ZM9.33398 21.3359C8.60065 21.3359 7.97287 21.0748 7.45065 20.5526C6.92843 20.0304 6.66732 19.4026 6.66732 18.6693V8.0026C6.66732 7.26927 6.92843 6.64149 7.45065 6.11927C7.97287 5.59705 8.60065 5.33594 9.33398 5.33594H28.0007C28.734 5.33594 29.3618 5.59705 29.884 6.11927C30.4062 6.64149 30.6673 7.26927 30.6673 8.0026V18.6693C30.6673 19.4026 30.4062 20.0304 29.884 20.5526C29.3618 21.0748 28.734 21.3359 28.0007 21.3359H9.33398ZM12.0007 18.6693H25.334C25.334 17.9359 25.5951 17.3082 26.1173 16.7859C26.6395 16.2637 27.2673 16.0026 28.0007 16.0026V10.6693C27.2673 10.6693 26.6395 10.4082 26.1173 9.88594C25.5951 9.36372 25.334 8.73594 25.334 8.0026H12.0007C12.0007 8.73594 11.7395 9.36372 11.2173 9.88594C10.6951 10.4082 10.0673 10.6693 9.33398 10.6693V16.0026C10.0673 16.0026 10.6951 16.2637 11.2173 16.7859C11.7395 17.3082 12.0007 17.9359 12.0007 18.6693ZM25.334 26.6693H4.00065C3.26732 26.6693 2.63954 26.4082 2.11732 25.8859C1.5951 25.3637 1.33398 24.7359 1.33398 24.0026V10.6693C1.33398 10.2915 1.46176 9.97483 1.71732 9.71927C1.97287 9.46372 2.28954 9.33594 2.66732 9.33594C3.0451 9.33594 3.36176 9.46372 3.61732 9.71927C3.87287 9.97483 4.00065 10.2915 4.00065 10.6693V24.0026H25.334C25.7118 24.0026 26.0284 24.1304 26.284 24.3859C26.5395 24.6415 26.6673 24.9582 26.6673 25.3359C26.6673 25.7137 26.5395 26.0304 26.284 26.2859C26.0284 26.5415 25.7118 26.6693 25.334 26.6693Z" fill="#079455" />
            </svg>
          }
          iconBg={iconBgGreen}
          title={t('inventory.kpiTotalCountedValue')}
          mobileLayout="tile"
          value={totalValue}
          valueType="currency"
          loading={isLoadingReview}
          subtitle={allProducts ? undefined : t('inventory.shareOfTotalValue', { share: sharePct })}
        />
        </div>
        <div className="h-full min-w-0" data-onboarding="inventory-section-kpis inventory-section-kpi-counted">
        <StatCard
          icon={
            <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]' viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_210_26321" maskUnits="userSpaceOnUse" x="2" y="2" width="28" height="28">
                <path d="M29 29V3H3V29H29Z" fill="white" stroke="white" strokeWidth="2" />
              </mask>
              <g mask="url(#mask0_210_26321)">
                <path d="M29.125 17.0938L22.5625 19.2813V29.1797L29.125 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 17.0938L22.5625 19.2813V29.1797L16 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M29.125 17.0938L22.5625 19.2812L16 17.0938L22.5625 14.9062L29.125 17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 17.0938L9.4375 19.2813V29.1797L16 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2.875 17.0938L9.4375 19.2813V29.1797L2.875 26.9922V17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 17.0938L9.4375 19.2812L2.875 17.0938L9.4375 14.9062L16 17.0938Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.5625 5.00781L16 7.19531V17.0938L22.5625 14.9063V5.00781Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.4375 5.00781L16 7.19531V17.0938L9.4375 14.9063V5.00781Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.5625 5.00781L16 7.19531L9.4375 5.00781L16 2.82031L22.5625 5.00781Z" stroke="#6938EF" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            </svg>
          }
          iconBg={iconBgPurple}
          title={t('inventory.kpiProductsCounted')}
          mobileLayout="tile"
          value={productsCounted}
          valueType="number"
          loading={isLoadingReview}
        />
        </div>
        <div className="h-full min-w-0" data-onboarding="inventory-section-kpis inventory-section-kpi-unlinked">
        <StatCard
          icon={
            <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]'  viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_210_26348" maskUnits="userSpaceOnUse" x="2" y="2" width="28" height="28">
                <path d="M29 29V3H3V29H29Z" fill="white" stroke="white" strokeWidth="2" />
              </mask>
              <g mask="url(#mask0_210_26348)">
                <path d="M16.8803 12.9011C17.3304 13.1574 17.7525 13.4794 18.1366 13.8634C20.4439 16.1707 20.507 19.8484 17.6043 22.7511L13.673 26.6824C10.7703 29.5851 7.09261 29.522 4.78535 27.2146C2.47808 24.9074 2.41486 21.2297 5.31756 18.327L8.17969 15.4531" stroke="#2E90FA" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M15.1198 19.0991C14.6696 18.8427 14.2476 18.5209 13.8634 18.1367C11.5562 15.8294 11.493 12.1518 14.3957 9.24908L18.327 5.31782C21.2297 2.41506 24.9074 2.47828 27.2147 4.78554C29.522 7.09286 29.5852 10.7705 26.6825 13.6732L23.8203 16.5471" stroke="#2E90FA" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            </svg>
          }
          iconBg="#2E90FA1A"
          title={t('inventory.kpiProductsNotLinked')}
          mobileLayout="tile"
          value={productsNotLinked}
          valueType="number"
          loading={isLoadingReview}
          onClick={() => document.getElementById('inventory-attention')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          showArrow
        />
        </div>
        <div className="h-full min-w-0" data-onboarding="inventory-section-kpis inventory-section-kpi-missing">
        <StatCard
          icon={
            <svg className='sm:w-8 sm:h-8 w-[30px] h-[25px]' viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.16602 8.33325H10.8327" stroke="#B54708" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4.16602 11.6667H10.8327" stroke="#B54708" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M15.834 14.673C14.8918 16.367 13.1773 17.5 11.2186 17.5C8.24476 17.5 5.83398 14.8883 5.83398 11.6667V8.33333C5.83398 5.11167 8.24476 2.5 11.2186 2.5C13.1773 2.5 14.8918 3.63299 15.834 5.32701" stroke="#B54708" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          }
          iconBg={iconBgAmber}
          title={t('inventory.kpiMissingPrice')}
          mobileLayout="tile"
          value={missingPriceCount}
          valueType="number"
          loading={isLoadingReview}
          onClick={() => document.getElementById('inventory-attention')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          showArrow
        />
        </div>
      </div>

      {/* Products in section and Value by Category side by side */}
      <div className={`mb-5 sm:mb-6 grid grid-cols-1 gap-4 sm:gap-6 ${isFullPageView ? '' : 'xl:grid-cols-3'}`}>
        {/* Products in section: max 10 rows per page with the inline pager; "See all"
            expands to full page view hiding other sections. */}
        <div className={isFullPageView ? '' : 'xl:col-span-2'}>
          {deleteError && (
            <p className={cn(smallTextSize, 'text-[#D92D20] dark:text-textRed-dark mb-2')}>{deleteError}</p>
          )}
          <div className="hidden md:block" data-onboarding="inventory-section-products">
            <TableComponent
              title={productsTitle}
              data={sortedProducts}
              columns={isFullPageView ? expandedColumns : baseColumns}
              itemsPerPage={isFullPageView ? undefined : 10}
              usePagination={isFullPageView}
              blankLines={false}
              headerActions={
                <div className="flex items-center gap-3 flex-wrap justify-end">
                  <TabbedSections
                    options={[
                      { value: 'counts', label: t('inventory.countModeCounts') },
                      { value: 'quantity', label: t('inventory.countModeQuantity') },
                    ]}
                    value={countMode}
                    onChange={(v) => setCountMode(v as ProductCountMode)}
                    isTimeOptionSelection={true}
                    onboardingId="inventory-section-mode"
                  />
                  <InventoryProductSearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={t('inventory.searchProducts')}
                  />
                  {/* Add a product staff forgot to count; needs the section's saved count row. */}
                  {sectionMeta?.countId && (
                    <button
                      type="button"
                      data-onboarding="inventory-section-add"
                      onClick={() => setIsAddProductOpen(true)}
                      className={cn(
                        smallTextSize,
                        semiBoldText,
                        'inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 transition-colors shrink-0'
                      )}
                    >
                      <Plus className="w-4 h-4" />
                      {t('inventory.addCountProductAction')}
                    </button>
                  )}
                </div>
              }
              seeAllText={!isFullPageView ? t('common.viewAll') : t('common.viewLess')}
              onSeeAllClick={() => setIsFullPageView(!isFullPageView)}
              seeAllOnboardingId="inventory-section-view-all"
              rowOnboardingId={(row) => {
                if (walkthroughAddedProductKey() === row.productKey) return 'inventory-section-added';
                if (walkthroughCountProduct()?.productKey === row.productKey) return 'inventory-section-product';
                return undefined;
              }}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              options={{ borderless: true, leftAlignCells: true, noBlankPadding: true }}
            />
          </div>

          <div className="md:hidden flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className={cn(semiBoldText, primaryText, 'text-[18px] leading-[24px]')}>
                {productsTitle}
              </h2>
            </div>

            <InventoryProductSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('inventory.searchProducts')}
            />

            {mobileDisplayProducts.length === 0 ? (
              <div className="flex items-center justify-center w-full py-8">
                <p className={cn(smallTextSize, regularText, quaternaryText, 'text-center w-full')}>
                  {t('common.noDataInPeriod')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {mobileDisplayProducts.map((row) => (
                  <InventoryProductMobileCard
                    // The same product counted in two sections is two rows in all-products
                    // mode, so the count it belongs to has to be part of the key.
                    key={`${row.countId}-${row.productKey}`}
                    row={row}
                    t={t}
                    categoryLabel={t('inventory.colProductCategory')}
                    categoryValue={productCategoryLabel(t, row.productCategory, row.mainCategory)}
                    actionHandlers={{
                      onRelink: setRelinkProduct,
                      onEditCustom: openCustomIngredientEditor,
                      onSetPrice: setSetPriceProduct,
                      onSetup: getMobileSetupHandler(row),
                    }}
                  />
                ))}
                {sortedProducts.length > mobileVisibleCount && (
                  <button
                    type="button"
                    onClick={() => setMobileVisibleCount((count) => count + MOBILE_PRODUCTS_BATCH)}
                    className={cn(smallTextSize, semiBoldText, textBlue, 'self-center py-1')}
                  >
                    {t('inventory.loadMore')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Value by category (click-to-filter the products table above) - hidden in full page view */}
        {!isFullPageView && (
          <div className="xl:col-span-1">
            <ValueByCategoryCard
              categoriesInventory={breakdown.inventory}
              categoriesProduct={breakdown.product}
              mode={categoryMode}
              onModeChange={handleModeChange}
              emptyText={sectionProducts.length === 0 ? t('inventory.noCountsEmptyValue') : undefined}
              activeRowId={activeCategory?.rowId ?? null}
              onSelect={handleCategorySelect}
              showBelowMin={false}
              display="pie"
            />
            {activeCategory && (
              <div className="mt-3">
                <button
                  onClick={() => setActiveCategory(null)}
                  className={cn('inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border', smallTextSize, semiBoldText, borderPrimary, lightGreenBG, greenTextColor)}
                >
                  <span>{t('inventory.filteredBy', { category: activeCategory.label })}</span>
                  <X className="w-3.5 h-3.5" />
                  <span>{t('inventory.clearFilter')}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Needs attention in this section (section-scoped, unfiltered by category) - hidden in full page view */}
      {!isFullPageView && (
        <div className="mb-6" id="inventory-attention">
          <InventoryAttentionCard
            title={allProducts ? t('inventory.attentionTitle') : t('inventory.attentionInSection', { section: sectionMeta?.sectionName ?? '' })}
            products={sectionProducts}
            emptyText={t('inventory.noCountsEmptyValue')}
            onRelink={setRelinkProduct}
            onEditCustom={openCustomIngredientEditor}
            onSetPrice={setSetPriceProduct}
            onSetup={setSetupProduct}
            costPriceKeys={costPriceKeys}
          />
        </div>
      )}

      {sectionMeta?.countId && (
        <AddCountProductModal
          isOpen={isAddProductOpen}
          onClose={() => setIsAddProductOpen(false)}
          countId={sectionMeta.countId}
          sectionId={sectionId ?? ''}
          sectionName={sectionMeta.sectionName}
          restaurantId={restaurantId}
          existingKeys={countedKeys}
        />
      )}

      <ReLinkInventoryProductModal
        product={relinkProduct ? relinkTargetFromReviewRow(relinkProduct) : null}
        restaurantId={restaurantId}
        onClose={() => setRelinkProduct(null)}
      />
      <SetInventoryPriceModal
        product={setPriceProduct ? setPriceTargetFromReviewRow(setPriceProduct) : null}
        restaurantId={restaurantId}
        onClose={() => setSetPriceProduct(null)}
      />
      {/* Mounted only while open — it lazily loads order + revenue history. Inventory
          rows key their order product by inventoryKey (their productKey); a counted
          subrecipe names the prep itself instead. */}
      {setupProduct && (
        <ProductSetupModal
          practice={!!practice}
          identity={{
            platform: setupProduct.supplier.platform ?? '',
            productName: setupProduct.productName,
            match: setupMatchForReviewRow(setupProduct),
          }}
          restaurantId={restaurantId}
          onClose={() => setSetupProduct(null)}
        />
      )}
      {/* Edit a custom ingredient straight from a product row — same modal as Manage ingredients. */}
      {editingCustomIngredient && (
        <CustomIngredientDetailModal
          ingredient={editingCustomIngredient}
          restaurantId={restaurantId}
          onClose={() => setEditingCustomIngredient(null)}
          onSaved={handleCustomIngredientSaved}
        />
      )}
    </div>
    </PracticeScopeProvider>
  );
};

export default InventorySectionDetailPage;
